/**
 * signOutAndRedirect — the ONE shared sign-out sequence for every explicit or
 * automatic sign-out on the app surface (shell nav-foot, profile screen,
 * Security Centre current-session revoke, idle auto-logout).
 *
 * Order matters:
 *   1. clearActAsContext() — the firm act-as / impersonation tokens are
 *      sessionStorage-backed (S-1.3) and must never survive a sign-out: a firm
 *      operator signing out while administering a client would otherwise leave
 *      a live act-as JWT behind for the next sign-in on this tab. Cleared
 *      FIRST so even a hung/failed network sign-out can't strand the tokens.
 *   2. supabase.auth.signOut() — errors swallowed deliberately: a failed
 *      network sign-out should still drop the local session and land the user
 *      on the sign-in screen rather than trap them.
 *   3. queryClient.clear() — drop the TanStack cache BEFORE the redirect so the
 *      login screen (and the next user to sign in on this shared/office tab)
 *      can never render the signed-out user's cached trust/estate/tenant data.
 *      The QueryClient is a module singleton that survives this client-side
 *      navigate (no page reload), so an explicit clear is the only thing that
 *      empties it. Belt-and-suspenders with the auth listener's SIGNED_OUT
 *      clear (onboarding-reset.ts) — this call guarantees the cache is empty
 *      even if the listener is not mounted at this instant.
 *   4. Navigate to /auth/login — the same target /app's auth gate uses for an
 *      unauthenticated session, so the redirect path stays consistent.
 *
 * NOT for the Security Centre's password-change re-auth
 * (`reauthClient.auth.signOut({ scope: 'local' })` on an isolated client) —
 * that is a deliberately scoped cleanup of a throwaway session, not a user
 * sign-out.
 */

import { clearActAsContext } from './act-as-context';
import { forgetLastKnownActAsSubject } from './acting/last-act-as';
import { supabase } from './supabase';
import { queryClient } from './query-client';

/** The slice of TanStack Router's `navigate` this helper needs — callers pass
 *  the function returned by `useNavigate()`. */
type NavigateToLogin = (opts: { to: '/auth/login' }) => Promise<void> | void;

export async function signOutAndRedirect(navigate: NavigateToLogin): Promise<void> {
  clearActAsContext();
  // AND FORGET WHO IT WAS (ruling 6, 2026-09-03). `clearActAsContext()` empties
  // the STORE; the sticky subject in `lib/acting/last-act-as.ts` survives it on
  // purpose, and survives an expiry on purpose. Signing out is a deliberate
  // exit, which its header names as the one place this call belongs. Without it
  // the next sign-in on this tab could be offered a re-entry into the previous
  // operator's client by /app's lapsed-act-as guard.
  forgetLastKnownActAsSubject();
  try {
    await supabase.auth.signOut();
  } catch {
    /* fall through to the redirect regardless */
  }
  // Purge the singleton query cache before landing on /auth/login so no stale
  // fiduciary data survives into the next session on this tab (HR-P0).
  queryClient.clear();
  await navigate({ to: '/auth/login' });
}
